import { logger } from './logger';
const DB_NAME = 'NotizyUploadRecovery';
const DB_VERSION = 2; // v2: aparte progress store zodat voortgang bijwerken de blob niet herschrijft
const FILE_UPLOADS_STORE = 'pending-file-uploads';
const PROGRESS_STORE = 'upload-progress';

export type PendingFileUploadStatus = 'pending_upload' | 'uploading';

export interface StoredPendingFileUploadMeta {
    id: string;
    fileName: string;
    createdAt: number;
    uploadedChunks: number;
    totalChunks: number;
    fileSize: number;
    mimeType: string;
    status: PendingFileUploadStatus;
}

interface StoredPendingFileUpload extends StoredPendingFileUploadMeta {
    blob: Blob;
}

interface StoredUploadProgress {
    id: string;
    uploadedChunks: number;
    totalChunks: number;
    status: PendingFileUploadStatus;
}

let cachedDbPromise: Promise<IDBDatabase> | null = null;

const openDatabase = (): Promise<IDBDatabase> => {
    if (cachedDbPromise) {
        return cachedDbPromise;
    }

    cachedDbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(FILE_UPLOADS_STORE)) {
                db.createObjectStore(FILE_UPLOADS_STORE, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(PROGRESS_STORE)) {
                db.createObjectStore(PROGRESS_STORE, { keyPath: 'id' });
            }
        };

        request.onsuccess = () => {
            const db = request.result;
            // Geef de gedeelde verbinding vrij zodra een andere tab een upgrade wil doen.
            db.onversionchange = () => {
                db.close();
                cachedDbPromise = null;
            };
            db.onclose = () => {
                cachedDbPromise = null;
            };
            resolve(db);
        };
        request.onerror = () => {
            cachedDbPromise = null;
            reject(request.error);
        };
    });

    return cachedDbPromise;
};

const runTransaction = <T>(
    mode: IDBTransactionMode,
    storeNames: string[],
    callback: (stores: { [name: string]: IDBObjectStore }) => Promise<T> | T
): Promise<T> => {
    return new Promise(async (resolve, reject) => {
        try {
            const db = await openDatabase();
            const transaction = db.transaction(storeNames, mode);

            const stores: { [name: string]: IDBObjectStore } = {};
            storeNames.forEach(name => {
                stores[name] = transaction.objectStore(name);
            });

            const result = await callback(stores);

            transaction.oncomplete = () => resolve(result);
            transaction.onerror = () => reject(transaction.error);
        } catch (error) {
            reject(error);
        }
    });
};

export const savePendingFileUpload = async (
    id: string,
    blob: Blob,
    metadata: Omit<StoredPendingFileUploadMeta, 'uploadedChunks' | 'totalChunks' | 'status'> & {
        uploadedChunks?: number;
        totalChunks?: number;
        status?: PendingFileUploadStatus;
    }
): Promise<void> => {
    const record: StoredPendingFileUpload = {
        id,
        blob,
        fileName: metadata.fileName,
        createdAt: metadata.createdAt ?? Date.now(),
        uploadedChunks: metadata.uploadedChunks ?? 0,
        totalChunks: metadata.totalChunks ?? 0,
        fileSize: metadata.fileSize ?? blob.size,
        mimeType: metadata.mimeType ?? blob.type ?? 'application/octet-stream',
        status: metadata.status ?? 'pending_upload'
    };

    await runTransaction('readwrite', [FILE_UPLOADS_STORE], (stores) => {
        stores[FILE_UPLOADS_STORE].put(record);
    });
};

export const getPendingFileUpload = async (id: string): Promise<(StoredPendingFileUploadMeta & { blob: Blob }) | null> => {
    try {
        let record: StoredPendingFileUpload | null = null;
        let progress: StoredUploadProgress | null = null;
        await runTransaction('readonly', [FILE_UPLOADS_STORE, PROGRESS_STORE], (stores) => {
            const recordRequest = stores[FILE_UPLOADS_STORE].get(id);
            const progressRequest = stores[PROGRESS_STORE].get(id);
            return new Promise<void>((resolve) => {
                let remaining = 2;
                const settle = () => {
                    remaining -= 1;
                    if (remaining === 0) {
                        resolve();
                    }
                };
                recordRequest.onsuccess = () => {
                    record = recordRequest.result ?? null;
                    settle();
                };
                progressRequest.onsuccess = () => {
                    progress = progressRequest.result ?? null;
                    settle();
                };
            });
        });

        if (record && progress) {
            const progressRecord = progress as StoredUploadProgress;
            return {
                ...(record as StoredPendingFileUpload),
                uploadedChunks: progressRecord.uploadedChunks,
                totalChunks: progressRecord.totalChunks,
                status: progressRecord.status
            };
        }

        return record;
    } catch (error) {
        logger.error('Failed to read pending file upload from IndexedDB:', error);
        return null;
    }
};

export const getPendingFileUploads = async (): Promise<StoredPendingFileUploadMeta[]> => {
    try {
        let records: StoredPendingFileUpload[] = [];
        let progressRecords: StoredUploadProgress[] = [];
        await runTransaction('readonly', [FILE_UPLOADS_STORE, PROGRESS_STORE], (stores) => {
            const recordsRequest = stores[FILE_UPLOADS_STORE].getAll();
            const progressRequest = stores[PROGRESS_STORE].getAll();
            return new Promise<void>((resolve) => {
                let remaining = 2;
                const settle = () => {
                    remaining -= 1;
                    if (remaining === 0) {
                        resolve();
                    }
                };
                recordsRequest.onsuccess = () => {
                    records = (recordsRequest.result as StoredPendingFileUpload[] | undefined) ?? [];
                    settle();
                };
                progressRequest.onsuccess = () => {
                    progressRecords = (progressRequest.result as StoredUploadProgress[] | undefined) ?? [];
                    settle();
                };
            });
        });

        const progressById = new Map(progressRecords.map(progress => [progress.id, progress]));
        return records
            .map(({ blob: _blob, ...record }) => {
                const progress = progressById.get(record.id);
                return progress
                    ? {
                        ...record,
                        uploadedChunks: progress.uploadedChunks,
                        totalChunks: progress.totalChunks,
                        status: progress.status
                    }
                    : record;
            })
            .sort((a, b) => a.createdAt - b.createdAt);
    } catch (error) {
        logger.error('Failed to list pending file uploads from IndexedDB:', error);
        return [];
    }
};

export const updatePendingFileUploadProgress = async (
    id: string,
    uploadedChunks: number,
    totalChunks: number
): Promise<void> => {
    // Voortgang staat in een eigen store zodat we niet per chunk het volledige
    // upload-record (inclusief blob) hoeven te herschrijven.
    const progress: StoredUploadProgress = {
        id,
        uploadedChunks,
        totalChunks,
        status: 'uploading'
    };
    await runTransaction('readwrite', [PROGRESS_STORE], (stores) => {
        stores[PROGRESS_STORE].put(progress);
    });
};

export const removePendingFileUpload = async (id: string): Promise<void> => {
    await runTransaction('readwrite', [FILE_UPLOADS_STORE, PROGRESS_STORE], (stores) => {
        stores[FILE_UPLOADS_STORE].delete(id);
        stores[PROGRESS_STORE].delete(id);
    });
};

export const RECOVERY_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 dagen

// Ruimt verlopen herstel-data op: uploads ouder dan de TTL, plus
// progress-records die geen bijbehorend upload-record meer hebben.
export const cleanupExpiredFileUploads = async (maxAgeMs: number = RECOVERY_TTL_MS): Promise<void> => {
    const cutoff = Date.now() - maxAgeMs;

    const records = await getPendingFileUploads();
    const expired = records.filter(record => (record.createdAt ?? 0) < cutoff);
    for (const record of expired) {
        logger.log(`Removing expired pending file upload: ${record.id}`);
        await removePendingFileUpload(record.id);
    }

    const validIds = new Set(
        records.filter(record => (record.createdAt ?? 0) >= cutoff).map(record => record.id)
    );

    await runTransaction('readwrite', [PROGRESS_STORE], (stores) => {
        const request = stores[PROGRESS_STORE].openKeyCursor();
        request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result as IDBCursor | null;
            if (cursor) {
                if (!validIds.has(cursor.primaryKey as string)) {
                    stores[PROGRESS_STORE].delete(cursor.primaryKey);
                }
                cursor.continue();
            }
        };
    });
};
